import { createJsonQuery } from '@farfetched/core'
import { runtypeContract } from '@farfetched/runtypes'
import { Array } from 'runtypes'
import { allGroupsUrl } from '@entities/groups/api'
import { Group, IGroup } from '@entities/groups/contract'
import { combine, createEvent, createStore, sample } from 'effector/compat'
import { useStore } from 'effector-react/compat'
import { $goods } from '@entities/goods/model'
import { $currentPath } from '@entities/route/model'

const isObjectEmpty = (objectName: object) => {
    for (const prop in objectName) {
        if (objectName.hasOwnProperty(prop)) {
            return false
        }
    }
    return true
}

export interface IGroupMap {
    [key: number]: IGroup
}

export const convertGroupToAssociativeArray = (allGroups: IGroup[]): IGroupMap => {
    return Object.assign(
        {},
        ...allGroups.map(({ id, level, priority, title, isGeneral, parentClsGroupId, goodsCount, imageEndPath }) => ({
            [id]: {
                id,
                title,
                level,
                priority,
                isGeneral,
                parentClsGroupId,
                goodsCount,
                imageEndPath
            } as IGroup
        }))
    )
}

export const allGroupsQuery = createJsonQuery({
    name: 'allGroupsQuery',
    request: {
        method: 'GET',
        url: () => allGroupsUrl()
    },
    initialData: [] as IGroup[],
    response: {
        contract: runtypeContract(Array(Group))
    }
})
export const $allGroups = allGroupsQuery.$data

export const $generalGroups = combine($allGroups, (groups) => groups.filter((x) => x.isGeneral))

export const $groupMap = combine({ groups: $allGroups }, ({ groups }) =>
    !groups || groups.length === 0 ? {} : convertGroupToAssociativeArray(groups)
)

export const $goodsGroupMap = combine(
    { groupMap: $groupMap, goods: $goods, currentPath: $currentPath },
    ({ groupMap, goods, currentPath }) => {
        if (isObjectEmpty(groupMap)) return {}
        if (
            !currentPath.includes('/catalog/search') &&
            !currentPath.includes('/catalog/liked') &&
            !currentPath.includes('/catalog/suggestions')
        )
            return groupMap
        // remove duplicate
        const goodsCategories = [
            ...new Map(goods.flatMap((x) => x.webData.goodGroups).map((item) => [item.clsGroupID, item])).values()
        ]
        // only id category without duplicate
        const categoryIds = goodsCategories.map((x) => x.clsGroupID)

        // groups from goods
        let goodsGroups: IGroup[] = categoryIds.map((x) => groupMap[x])

        // pull parent groups
        const lowLevelGroups: IGroup[] = goodsGroups.filter((x) => x.level > 0 && x.parentClsGroupId)

        let parentLowLevelGroups: IGroup[] = [
            ...new Map(
                lowLevelGroups.map((x) => [groupMap[x.parentClsGroupId!].id, groupMap[x.parentClsGroupId!]])
            ).values()
        ]

        goodsGroups = goodsGroups.concat(parentLowLevelGroups)
        try {
            while (parentLowLevelGroups.some((x) => x.level > 0 && x.parentClsGroupId)) {
                parentLowLevelGroups = [
                    ...new Map(
                        parentLowLevelGroups
                            .filter((x) => x.level > 0 && x.parentClsGroupId)
                            .map((x) => [groupMap[x.parentClsGroupId!].id, groupMap[x.parentClsGroupId!]])
                    ).values()
                ]

                goodsGroups = goodsGroups.concat(parentLowLevelGroups)
            }
        } catch (e) {
            console.error(e)
        }

        // remove duplicate
        goodsGroups = [...new Map(goodsGroups.map((item) => [item.id, item])).values()]
        if (!goodsGroups || goodsGroups.length === 0) return {}
        return convertGroupToAssociativeArray(goodsGroups)
    }
)

export const $currentGroupId = createStore<number>(0)
export const setCurrentGroupId = createEvent<number>()

sample({
    clock: setCurrentGroupId,
    target: $currentGroupId
})

/*sample({
    clock: onRouteChange,
   /!* filter: (path) => {
        if (!path.includes('/catalog/')) return true
        if (path.includes('/search')) return true
        return !!(path.includes('/catalog/') && path.includes('/search') && path.includes('/liked') && path.includes('/promotion'))

    },*!/
    target: $currentGroupId.reinit!
})*/

export const useCurrentGroupId = () => useStore($currentGroupId)
